/**
 * Error codes → text a rider can act on.
 *
 * The backend's `error.message` is not uniformly presentable: some handlers write a
 * Russian sentence aimed at the person on the other end (pricing, service-area, hold
 * failures), while the register/car validation family renders `Display`, which is
 * English and technical — «duplicate plate: А123ВС77». So the rule here is: map by
 * `code` first, and only fall back to the server's sentence for codes we have not
 * claimed. That keeps the server free to add new failure modes without this file
 * turning them into «Что-то пошло не так».
 *
 * Wording follows the mobile clients where they already say something, so a rider who
 * uses both does not meet two different explanations of the same refusal.
 */
import { ApiError, LEGAL_ACCEPTANCE_REQUIRED } from './client';

const BY_CODE: Record<string, string> = {
  network_error: 'Нет связи с сервером. Проверьте интернет и попробуйте ещё раз',

  // Login and session
  invalid_credentials: 'Неверный email или пароль',
  account_locked:
    'Слишком много неудачных попыток. Вход в аккаунт временно заблокирован — попробуйте позже',
  rate_limited: 'Слишком много попыток. Подождите немного и попробуйте снова',
  captcha_required: 'Введите код с картинки',
  token_invalid: 'Сессия истекла, войдите заново',
  unauthorized: 'Сессия истекла, войдите заново',
  account_suspended: 'Аккаунт заблокирован. Напишите в поддержку',

  // Registration
  invalid_email: 'Проверьте адрес почты — он выглядит неправильно',
  weak_password: 'Пароль должен быть не короче 8 символов',
  consent_required: 'Чтобы продолжить, отметьте оба согласия',
  email_already_registered: 'На эту почту уже зарегистрирован аккаунт. Войдите в него',

  // Cars
  duplicate_plate: 'Техника с таким номером уже добавлена',
  last_car_undeletable:
    'Это вся ваша техника — без неё нельзя оформить заказ. Сначала добавьте ещё одну',
  tank_capacity_invalid: 'Проверьте объём бака',

  // Account deletion
  account_deletion_blocked: 'Сейчас идёт заказ. Удалить аккаунт можно после его завершения',

  // Смена почты
  current_password_required: 'Чтобы сменить адрес, введите текущий пароль',

  // Legal. Rarely seen: the acceptance screen opens on this code, so the text is what shows for
  // the split second before the navigation lands — and for the case where it could not.
  legal_acceptance_required: 'Чтобы продолжить, примите условия сервиса',
  // Про «один раз» — теми же словами, что и сервер (`email_not_verified_message` в
  // consumer/me.rs): просьба подтвердить почту посреди оформления читается как новый постоянный
  // барьер, и человек бросает заказ. Строка запасная — обычно успевает открыться сам экран
  // подтверждения, — но если не успела, обещание должно прозвучать и здесь.
  email_not_verified:
    'Чтобы оформить заказ, подтвердите адрес почты — на него придёт чек. Подтвердить нужно один раз',

  // Server-side trouble
  db_error: 'Что-то пошло не так. Попробуйте ещё раз через минуту',
  internal: 'Что-то пошло не так. Попробуйте ещё раз через минуту',
  overloaded: 'Сервер перегружен. Попробуйте через несколько секунд',
};

/**
 * Codes whose `message` is written FOR the person on the other end and is safe to show as-is.
 *
 * This is a whitelist rather than «anything not in the map above», and the difference matters.
 * Most variants render `Display`, which is English and prefixed — «duplicate plate: А123ВС77»,
 * «not found». Exactly these seven pass the handler's own sentence through untouched
 * (`m.clone()` in crates/api/src/error.rs), and several of them — pricing gaps, service-area
 * refusals, declined holds — say something specific that we could not reconstruct here.
 */
const MESSAGE_IS_FOR_THE_USER = new Set([
  'bad_request',
  'bad_gateway',
  'service_area_unavailable',
  'hold_network_error',
  // Сервер сам пишет здесь фразу для человека: «оплата не проходит, это на нашей стороне».
  'payment_gateway_access',
  'hold_declined',
  // «Похоже, включён VPN…» — сервер пишет фразу для человека, и в ней сказано, что делать.
  'vpn_suspected',
  'active_order_exists_for_car',
  'account_suspended',
  // «Сейчас мы не работаем: «Срочно» и «Ближайшая» принимаем с 09:00 до 22:00…» — фраза пятна,
  // она называет и часы, и ближайшее окно «Ко времени».
  'asap_closed',
]);

/** Text to show for a failed request, whatever it turned out to be. */
export function friendlyErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code !== undefined && error.code in BY_CODE) return BY_CODE[error.code] as string;
    if (
      error.code !== undefined &&
      MESSAGE_IS_FOR_THE_USER.has(error.code) &&
      error.message.length > 0 &&
      !/^Ошибка \d+$/.test(error.message)
    ) {
      return error.message;
    }
  }
  return 'Что-то пошло не так. Попробуйте ещё раз';
}

/**
 * True when the server refused because the current legal redaction has not been accepted.
 *
 * Matches on the CODE alone. A screen that owns a draft — the order wizard, the add-car
 * screen — should check this before showing an error at all: the transport has already asked
 * the app to open the acceptance screen, and after the acceptance the person comes back to
 * exactly this URL, so the right local behaviour is to keep the draft and stay quiet.
 */
export function isLegalAcceptanceRequired(error: unknown): boolean {
  return error instanceof ApiError && error.code === LEGAL_ACCEPTANCE_REQUIRED;
}

/** True when the failure means "not signed in", as opposed to "this request failed". */
export function isAuthError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.code === 'token_invalid');
}
